import { useState, useEffect, useMemo, useCallback } from 'react';
import api, {
  getCompensationEntries, getCompensationSummary, createCompensationEntry,
  createCompensationEntryAdmin, updateCompensationStatus, bulkUpdateCompensationStatus,
  deleteCompensationEntry, generateProjectCompensations, bulkDeleteCompensations,
  getCompensationTypes, getProjects, getUsers, getAllUsersForDisplay
} from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import {
  Plus, MoreHorizontal, Check, X, DollarSign, Clock, CheckCircle2,
  Ban, Wallet, Loader2, Search, Filter, ChevronDown, ArrowUpDown, Coins,
  ChevronsUpDown, Pencil, History, ExternalLink, RefreshCw, Trash2
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { format } from 'date-fns';
import {
  Popover, PopoverContent, PopoverTrigger
} from '../components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList
} from '../components/ui/command';

const STATUS_CONFIG = {
  in_attesa: { label: 'In Attesa', color: 'bg-slate-100 text-slate-700 border-slate-200', icon: Clock },
  maturato: { label: 'Maturato', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: DollarSign },
  approvato: { label: 'Approvato', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  saldato: { label: 'Saldato', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Wallet },
  non_approvato: { label: 'Non Approvato', color: 'bg-red-100 text-red-700 border-red-200', icon: Ban },
};

const BUCKET_LABELS = { fisso: 'Fisso', variabile: 'Variabile' };

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.in_attesa;
  const Icon = cfg.icon;
  return (
    <Badge variant="outline" className={`${cfg.color} text-xs font-medium gap-1`} data-testid={`status-badge-${status}`}>
      <Icon className="h-3 w-3" />
      {cfg.label}
    </Badge>
  );
}

function SummaryCards({ summary, isAdmin }) {
  const cards = [
    { key: 'maturato', label: 'Da Approvare', icon: DollarSign, accent: 'text-amber-600' },
    { key: 'approvato', label: 'Approvati', icon: CheckCircle2, accent: 'text-emerald-600' },
    { key: 'saldato', label: 'Saldati', icon: Wallet, accent: 'text-blue-600' },
    { key: 'in_attesa', label: 'In Attesa', icon: Clock, accent: 'text-slate-500' },
  ];
  if (!isAdmin) {
    cards.splice(0, 1, { key: 'maturato', label: 'Maturati', icon: DollarSign, accent: 'text-amber-600' });
  }

  // Calculate total across all statuses
  const totalAmount = Object.values(summary).reduce((s, v) => s + (v.total || 0), 0);
  const totalCount = Object.values(summary).reduce((s, v) => s + (v.count || 0), 0);

  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
      {cards.map(c => {
        const data = summary[c.key] || { total: 0, count: 0 };
        return (
          <Card key={c.key} className="border border-gray-200" data-testid={`summary-card-${c.key}`}>
            <CardContent className="pt-4 pb-4 px-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{c.label}</span>
                <c.icon className={`h-4 w-4 ${c.accent}`} />
              </div>
              <p className="text-xl font-bold text-gray-900">
                {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(data.total)}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">{data.count} {data.count === 1 ? 'voce' : 'voci'}</p>
            </CardContent>
          </Card>
        );
      })}
      <Card className="border border-gray-800 bg-gray-900" data-testid="summary-card-totale">
        <CardContent className="pt-4 pb-4 px-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Totale</span>
            <Coins className="h-4 w-4 text-white" />
          </div>
          <p className="text-xl font-bold text-white">
            {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(totalAmount)}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">{totalCount} {totalCount === 1 ? 'voce' : 'voci'}</p>
        </CardContent>
      </Card>
    </div>
  );
}

export default function CompensationsPage() {
  const { user, hasRole } = useAuth();
  const { toast } = useToast();
  const isAdmin = hasRole(['admin']);
  const isHr = hasRole(['hr']);
  // canViewAll: vede tutti i compensi come admin (admin OR hr). HR è però in SOLA LETTURA.
  const canViewAll = isAdmin || isHr;

  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [compTypes, setCompTypes] = useState([]);

  // Filters
  const [filterMonth, setFilterMonth] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterBucket, setFilterBucket] = useState('all');
  const [filterUserId, setFilterUserId] = useState('all');
  const [filterProjectId, setFilterProjectId] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Selection for bulk actions
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Sort direction for date column
  const [sortDir, setSortDir] = useState('asc');

  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [recalcLoading, setRecalcLoading] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectEntryId, setRejectEntryId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  // Edit dialog (admin can modify all fields + leave a note)
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editUserId, setEditUserId] = useState('');
  const [editCompTypeId, setEditCompTypeId] = useState('');
  const [editRefMonth, setEditRefMonth] = useState('');
  const [editRefInfo, setEditRefInfo] = useState('');
  const [editNote, setEditNote] = useState('');
  // History dialog
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [historyEntry, setHistoryEntry] = useState(null);

  // Create form
  const [newEntry, setNewEntry] = useState({
    project_id: '', compensation_type_id: '', amount: '', reference_month: format(new Date(), 'yyyy-MM'), description: ''
  });
  const [newEntryAdmin, setNewEntryAdmin] = useState({
    project_id: '', product_id: '', user_id: '',
    compensation_type_id: '', amount: '',
    reference_month: format(new Date(), 'yyyy-MM'), reference_info: '', description: ''
  });
  const [projectSearch, setProjectSearch] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterMonth && filterMonth !== 'all') params.month = filterMonth;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterBucket !== 'all') params.bucket = filterBucket;
      if (canViewAll && filterUserId !== 'all') params.user_id = filterUserId;
      if (filterProjectId !== 'all') params.project_id = filterProjectId;

      const entriesRes = await getCompensationEntries(params);
      setEntries(entriesRes.data || []);
    } catch (err) {
      console.error(err);
      toast({ title: 'Errore', description: 'Impossibile caricare i compensi', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [filterMonth, filterStatus, filterBucket, filterUserId, filterProjectId, canViewAll, toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRecalcVariable = useCallback(async () => {
    const scopeMonth = (filterMonth && filterMonth !== 'all') ? filterMonth : null;
    const msg = scopeMonth
      ? `Ricalcolare i compensi variabili (in_attesa/maturato) di ${scopeMonth}? I compensi approvati/saldati NON saranno toccati.`
      : `Ricalcolare TUTTI i compensi variabili (in_attesa/maturato) di tutti i periodi? I compensi approvati/saldati NON saranno toccati.`;
    if (!window.confirm(msg)) return;
    setRecalcLoading(true);
    try {
      const res = await api.post('/compensations/recalculate-variable', scopeMonth ? { reference_month: scopeMonth } : {});
      toast({
        title: 'Ricalcolo completato',
        description: res.data?.message || `Cancellati ${res.data?.deleted}, rigenerati ${res.data?.regenerated}.`,
      });
      await loadData();
    } catch (err) {
      toast({ title: 'Errore', description: err?.response?.data?.detail || 'Operazione fallita', variant: 'destructive' });
    } finally {
      setRecalcLoading(false);
    }
  }, [filterMonth, loadData, toast]);

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [projRes, typesRes] = await Promise.all([
          getProjects(),
          getCompensationTypes()
        ]);
        setProjects(projRes.data || []);
        setCompTypes(typesRes.data || []);
        if (canViewAll) {
          const usersRes = await getAllUsersForDisplay();
          setUsers(usersRes.data || []);
        }
      } catch (err) {
        console.error(err);
      }
    };
    loadMeta();
  }, [canViewAll]);

  // Generate months for filter
  const months = useMemo(() => {
    const result = [{ value: 'all', label: 'Tutti i mesi' }];
    const now = new Date();
    for (let i = -2; i <= 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const val = format(d, 'yyyy-MM');
      const label = format(d, 'MMMM yyyy').replace(/^\w/, c => c.toUpperCase());
      result.push({ value: val, label });
    }
    return result;
  }, []);

  // Filtered entries
  const filteredEntries = useMemo(() => {
    if (!searchQuery) return entries;
    const q = searchQuery.toLowerCase();
    return entries.filter(e =>
      (e.user_name || '').toLowerCase().includes(q) ||
      (e.product_name || '').toLowerCase().includes(q) ||
      (e.client_name || '').toLowerCase().includes(q) ||
      (e.compensation_type_name || '').toLowerCase().includes(q) ||
      (e.reference_info || '').toLowerCase().includes(q)
    );
  }, [entries, searchQuery]);

  // Summary computed from filtered entries (reflects all filters)
  const userSummary = useMemo(() => {
    const result = {};
    for (const e of filteredEntries) {
      if (!result[e.status]) result[e.status] = { total: 0, count: 0 };
      result[e.status].total += e.amount;
      result[e.status].count += 1;
    }
    return result;
  }, [filteredEntries]);

  // Sorted entries for the table
  const sortedEntries = useMemo(() => {
    return [...filteredEntries].sort((a, b) => {
      const cmp = a.reference_month.localeCompare(b.reference_month);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filteredEntries, sortDir]);

  const handleToggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === filteredEntries.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredEntries.map(e => e.id)));
    }
  };

  const handleBulkAction = async (action, rejectionReason = '') => {
    if (selectedIds.size === 0) return;
    if (action === 'non_approvato' && !rejectionReason) {
      setRejectEntryId('__bulk__');
      setShowRejectDialog(true);
      return;
    }
    try {
      await bulkUpdateCompensationStatus({
        entry_ids: Array.from(selectedIds),
        status: action,
        ...(rejectionReason ? { rejection_reason: rejectionReason } : {}),
      });
      toast({ title: 'Aggiornato', description: `${selectedIds.size} compensi aggiornati` });
      setSelectedIds(new Set());
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Errore aggiornamento', variant: 'destructive' });
    }
  };

  const handleBulkDelete = async () => {
    const selected = filteredEntries.filter(e => selectedIds.has(e.id));
    const locked = selected.filter(e => ['approvato', 'saldato'].includes(e.status)).length;
    const msg = `Eliminare ${selected.length - locked} compensi selezionati?` + (locked ? `\n${locked} approvati/saldati verranno mantenuti.` : '');
    if (!window.confirm(msg)) return;
    try {
      const res = await bulkDeleteCompensations({ entry_ids: Array.from(selectedIds) });
      toast({ title: 'Eliminati', description: res.data.message });
      setSelectedIds(new Set());
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Errore eliminazione', variant: 'destructive' });
    }
  };

  const handleStatusChange = async (entryId, newStatus, rejectionReason = '') => {
    try {
      const body = { status: newStatus };
      if (rejectionReason) body.rejection_reason = rejectionReason;
      await updateCompensationStatus(entryId, body);
      toast({ title: 'Aggiornato' });
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Errore', variant: 'destructive' });
    }
  };

  const handleReject = async () => {
    if (!rejectEntryId) return;
    if (rejectEntryId === '__bulk__') await handleBulkAction('non_approvato', rejectReason || '-');
    else await handleStatusChange(rejectEntryId, 'non_approvato', rejectReason);
    setShowRejectDialog(false);
    setRejectEntryId(null);
    setRejectReason('');
  };

  const openEditDialog = (entry) => {
    setEditEntry(entry);
    setEditAmount(String(entry.amount ?? ''));
    setEditUserId(entry.user_id || '');
    setEditCompTypeId(entry.compensation_type_id || '');
    setEditRefMonth(entry.reference_month || '');
    setEditRefInfo(entry.reference_info || '');
    setEditNote('');
    setShowEditDialog(true);
  };

  const handleEditSave = async () => {
    if (!editEntry) return;
    const amt = parseFloat(editAmount);
    if (isNaN(amt) || amt < 0) {
      toast({ title: 'Importo non valido', variant: 'destructive' });
      return;
    }
    if (!editNote.trim()) {
      toast({ title: 'Nota obbligatoria', description: 'Inserisci il motivo della modifica.', variant: 'destructive' });
      return;
    }
    try {
      await updateCompensationStatus(editEntry.id, {
        amount: amt,
        user_id: editUserId || null,
        compensation_type_id: editCompTypeId || null,
        reference_month: editRefMonth || null,
        reference_info: editRefInfo,
        modification_note: editNote.trim(),
      });
      toast({ title: 'Compenso modificato' });
      setShowEditDialog(false);
      setEditEntry(null);
      setEditAmount('');
      setEditUserId('');
      setEditCompTypeId('');
      setEditRefMonth('');
      setEditRefInfo('');
      setEditNote('');
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Errore', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Confermi l\'eliminazione?')) return;
    try {
      await deleteCompensationEntry(id);
      toast({ title: 'Eliminato' });
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Errore', variant: 'destructive' });
    }
  };

  const handleCreate = async () => {
    try {
      if (isAdmin) {
        await createCompensationEntryAdmin({
          ...newEntryAdmin,
          amount: parseFloat(newEntryAdmin.amount)
        });
      } else {
        await createCompensationEntry({
          ...newEntry,
          amount: parseFloat(newEntry.amount)
        });
      }
      toast({ title: 'Compenso creato' });
      setShowCreateDialog(false);
      setNewEntry({ project_id: '', compensation_type_id: '', amount: '', reference_month: format(new Date(), 'yyyy-MM'), description: '' });
      setNewEntryAdmin({ project_id: '', product_id: '', user_id: '', compensation_type_id: '', amount: '', reference_month: format(new Date(), 'yyyy-MM'), reference_info: '', description: '' });
      setProjectSearch('');
      loadData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') : 'Errore creazione';
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    }
  };

  const fmtCurrency = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);

  const projectMap = useMemo(() => {
    const m = {};
    projects.forEach(p => { m[p.id] = p.name; });
    return m;
  }, [projects]);

  return (
    <div className="space-y-6" data-testid="compensations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="compensations-title">
            {canViewAll ? 'Gestione Compensi' : 'I Miei Compensi'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {isAdmin ? 'Visualizza, approva e gestisci tutti i compensi' : (isHr ? 'Visualizza tutti i compensi (sola lettura)' : 'Visualizza e richiedi compensi')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button
              variant="outline"
              onClick={handleRecalcVariable}
              disabled={recalcLoading}
              data-testid="recalc-variable-btn"
              title="Cancella e rigenera i compensi variabili (in_attesa/maturato) dalle vendite, applicando la % alla Success Fee"
            >
              {recalcLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Ricalcola Variabili
            </Button>
          )}
          {!isHr && (
            <Button onClick={() => setShowCreateDialog(true)} data-testid="create-compensation-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nuovo Compenso
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <SummaryCards summary={userSummary} isAdmin={canViewAll} />

      {/* Filters */}
      <Card className="border border-gray-200">
        <CardContent className="pt-4 pb-4 px-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <Select value={filterMonth} onValueChange={setFilterMonth}>
                <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="filter-month">
                  <SelectValue placeholder="Mese" />
                </SelectTrigger>
                <SelectContent>
                  {months.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[160px] h-9 text-sm" data-testid="filter-status">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterBucket} onValueChange={setFilterBucket}>
              <SelectTrigger className="w-[140px] h-9 text-sm" data-testid="filter-bucket">
                <SelectValue placeholder="Bucket" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="fisso">Fisso</SelectItem>
                <SelectItem value="variabile">Variabile</SelectItem>
              </SelectContent>
            </Select>

            {canViewAll && (
              <SearchableSelect
                value={filterUserId}
                onChange={setFilterUserId}
                options={users.map(u => ({ value: u.id, label: u.name || u.email }))}
                placeholder="Utente"
                allLabel="Tutti gli utenti"
                testId="filter-user"
                className="w-[180px]"
              />
            )}

            <SearchableSelect
              value={filterProjectId}
              onChange={setFilterProjectId}
              options={projects.map(p => ({ value: p.id, label: p.name }))}
              placeholder="Progetto"
              allLabel="Tutti i progetti"
              testId="filter-project"
              className="w-[180px]"
            />

            <div className="relative ml-auto">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Cerca..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-8 h-9 w-[200px] text-sm"
                data-testid="search-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions (Admin only) */}
      {isAdmin && selectedIds.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 bg-orange-50 border border-orange-200 rounded-lg" data-testid="bulk-actions-bar">
          <span className="text-sm font-medium text-orange-800">{selectedIds.size} selezionati</span>
          <Button size="sm" variant="outline" onClick={() => handleBulkAction('approvato')} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="bulk-approve-btn">
            <Check className="h-3.5 w-3.5 mr-1" /> Approva
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleBulkAction('saldato')} className="border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="bulk-pay-btn">
            <Wallet className="h-3.5 w-3.5 mr-1" /> Segna Saldato
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" data-testid="bulk-change-status-btn">
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> Cambia stato
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <DropdownMenuItem key={key} onClick={() => handleBulkAction(key)} data-testid={`bulk-status-${key}`}>
                  <cfg.icon className="h-4 w-4 mr-2" /> {cfg.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button size="sm" variant="outline" onClick={handleBulkDelete} className="border-red-300 text-red-700 hover:bg-red-50" data-testid="bulk-delete-btn">
            <Trash2 className="h-3.5 w-3.5 mr-1" /> Elimina
          </Button>
          <Button size="sm" variant="outline" onClick={() => setSelectedIds(new Set())} className="ml-auto" data-testid="bulk-deselect-btn">
            Deseleziona
          </Button>
        </div>
      )}

      {/* Table */}
      <Card className="border border-gray-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : filteredEntries.length === 0 ? (
            <div className="text-center py-20 text-gray-400" data-testid="empty-state">
              <DollarSign className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">Nessun compenso trovato</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50/50">
                    {isAdmin && (
                      <TableHead className="w-10">
                        <Checkbox
                          checked={selectedIds.size === filteredEntries.length && filteredEntries.length > 0}
                          onCheckedChange={handleSelectAll}
                          data-testid="select-all-checkbox"
                        />
                      </TableHead>
                    )}
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500 cursor-pointer select-none hover:text-gray-700" onClick={() => setSortDir(prev => prev === 'asc' ? 'desc' : 'asc')} data-testid="sort-month-header">
                      Mese <ArrowUpDown className="h-3 w-3 inline ml-0.5 opacity-60" />
                    </TableHead>
                    {canViewAll && <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">Persona</TableHead>}
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">Progetto</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">Cliente</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">Prodotto</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">Tipologia</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">Bucket</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">Riferimento</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500 text-right">Importo</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">Stato</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500 w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedEntries.map(entry => (
                    <TableRow key={entry.id} className="hover:bg-gray-50/50" data-testid={`comp-row-${entry.id}`}>
                      {isAdmin && (
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.has(entry.id)}
                            onCheckedChange={() => handleToggleSelect(entry.id)}
                          />
                        </TableCell>
                      )}
                      <TableCell className="text-sm font-medium text-gray-900">{entry.reference_month}</TableCell>
                      {canViewAll && (
                        <TableCell>
                          <div className="text-sm font-medium text-gray-900">{entry.user_name || '-'}</div>
                          <div className="text-xs text-gray-400 capitalize">{entry.role}</div>
                        </TableCell>
                      )}
                      <TableCell className="text-sm text-gray-700">{projectMap[entry.project_id] || entry.project_id?.slice(0, 8)}</TableCell>
                      <TableCell className="text-sm text-gray-500">{entry.client_name || '-'}</TableCell>
                      <TableCell className="text-sm text-gray-600">{entry.product_name || '-'}</TableCell>
                      <TableCell>
                        <span className="text-sm text-gray-600">{entry.compensation_type_name || '-'}</span>
                        {entry.is_manual && (
                          <Badge variant="outline" className="ml-1.5 text-[10px] px-1.5 py-0 bg-violet-50 text-violet-600 border-violet-200">Manuale</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={entry.bucket === 'fisso' ? 'bg-cyan-50 text-cyan-700 border-cyan-200 text-xs' : 'bg-purple-50 text-purple-700 border-purple-200 text-xs'}>
                          {BUCKET_LABELS[entry.bucket] || entry.bucket}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {entry.origin === 'da_success_fee' && entry.origin_id ? (
                          (() => {
                            const isSF = String(entry.reference_info || '').toLowerCase().startsWith('success fee');
                            const url = isSF
                              ? `/commissions?highlight=${entry.origin_id}`
                              : `/sales?highlight=${entry.origin_id}`;
                            const showOrigin = entry.origin_user_name && entry.origin_user_id && entry.origin_user_id !== entry.user_id;
                            return (
                              <div className="flex flex-col">
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-orange-600 hover:text-orange-700 hover:underline inline-flex items-center gap-1"
                                  title={isSF ? 'Apri la Success Fee di origine in nuova scheda' : 'Apri la vendita di origine in nuova scheda'}
                                  data-testid={`comp-ref-link-${entry.id}`}
                                >
                                  {(entry.reference_info || (isSF ? 'Success Fee' : 'Vendita')).split('·')[0].trim()}
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                                {showOrigin && (
                                  <span className="text-[11px] text-gray-400 mt-0.5" title={`Compenso derivato da ${isSF ? 'Success Fee' : 'vendita'} di ${entry.origin_user_name}`}>
                                    da: <b className="text-gray-600">{entry.origin_user_name}</b>
                                  </span>
                                )}
                              </div>
                            );
                          })()
                        ) : (
                          entry.reference_info || entry.description || '-'
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-sm font-semibold text-gray-900">{fmtCurrency(entry.amount)}</span>
                        {entry.was_modified && (
                          <button
                            type="button"
                            onClick={() => { setHistoryEntry(entry); setShowHistoryDialog(true); }}
                            className="ml-1 inline-flex items-center text-amber-500 hover:text-amber-600"
                            title="Compenso modificato - clicca per vedere lo storico"
                            data-testid={`modified-flag-${entry.id}`}
                          >
                            <History className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={entry.status} />
                        {entry.status === 'non_approvato' && entry.rejection_reason && (
                          <p className="text-[10px] text-red-400 mt-0.5 max-w-[120px] truncate" title={entry.rejection_reason}>
                            {entry.rejection_reason}
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        {isAdmin && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`comp-actions-${entry.id}`}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              {entry.status !== 'saldato' && (
                                <DropdownMenuItem onClick={() => openEditDialog(entry)} data-testid={`edit-comp-${entry.id}`}>
                                  <Pencil className="h-4 w-4 mr-2 text-blue-600" /> Modifica
                                </DropdownMenuItem>
                              )}
                              {Object.entries(STATUS_CONFIG).filter(([key]) => key !== entry.status).map(([key, cfg]) => (
                                <DropdownMenuItem
                                  key={key}
                                  onClick={() => (key === 'non_approvato' ? (setRejectEntryId(entry.id), setShowRejectDialog(true)) : handleStatusChange(entry.id, key))}
                                  data-testid={`comp-status-${key}-${entry.id}`}
                                >
                                  <cfg.icon className="h-4 w-4 mr-2" /> Segna {cfg.label}
                                </DropdownMenuItem>
                              ))}
                              {['in_attesa', 'maturato'].includes(entry.status) && (
                                <DropdownMenuItem onClick={() => handleDelete(entry.id)} className="text-red-600">
                                  <X className="h-4 w-4 mr-2" /> Elimina
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                        {!isAdmin && entry.status === 'non_approvato' && entry.rejection_reason && (
                          <span className="text-xs text-red-500" title={entry.rejection_reason}>Motivo: {entry.rejection_reason}</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Rifiuta Compenso</DialogTitle>
            <DialogDescription>Inserisci la motivazione del rifiuto (visibile all'utente)</DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            placeholder="Motivazione..."
            data-testid="reject-reason-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Annulla</Button>
            <Button variant="destructive" onClick={handleReject} data-testid="confirm-reject-btn">Rifiuta</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog - admin modifies all fields */}
      <Dialog open={showEditDialog} onOpenChange={(v) => { setShowEditDialog(v); if (!v) { setEditEntry(null); setEditAmount(''); setEditUserId(''); setEditCompTypeId(''); setEditRefMonth(''); setEditRefInfo(''); setEditNote(''); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-compensation-dialog">
          <DialogHeader>
            <DialogTitle>Modifica Compenso</DialogTitle>
            <DialogDescription>
              Modifica i campi e indica il motivo della modifica. Lo storico delle modifiche viene mantenuto e visibile.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {editEntry && (
              <div className="text-xs text-gray-500 bg-gray-50 rounded p-2 space-y-0.5">
                <div><span className="font-medium text-gray-700">Progetto:</span> {editEntry.client_name || '-'} - {editEntry.product_name || '-'}</div>
                <div><span className="font-medium text-gray-700">Stato attuale:</span> {STATUS_CONFIG[editEntry.status]?.label || editEntry.status}</div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs">Utente</Label>
              <Select value={editUserId} onValueChange={setEditUserId}>
                <SelectTrigger data-testid="edit-user-select"><SelectValue placeholder="Seleziona utente" /></SelectTrigger>
                <SelectContent>
                  {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Tipologia (definisce bucket)</Label>
                <Select value={editCompTypeId} onValueChange={setEditCompTypeId}>
                  <SelectTrigger data-testid="edit-type-select"><SelectValue placeholder="Tipologia" /></SelectTrigger>
                  <SelectContent>
                    {compTypes.filter(t => t.is_active !== false).map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name} ({t.bucket})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Mese Riferimento</Label>
                <Input type="month" value={editRefMonth} onChange={e => setEditRefMonth(e.target.value)} data-testid="edit-month-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-amount" className="text-xs">Importo (EUR) *</Label>
              <Input
                id="edit-amount"
                type="number"
                step="0.01"
                min="0"
                value={editAmount}
                onChange={e => setEditAmount(e.target.value)}
                data-testid="edit-amount-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Riferimento / Descrizione</Label>
              <Input value={editRefInfo} onChange={e => setEditRefInfo(e.target.value)} placeholder="Es. Rata 2/12" data-testid="edit-ref-input" />
            </div>
            <div className="space-y-1.5 border-t pt-3 mt-2">
              <Label htmlFor="edit-note" className="text-xs">Motivo della modifica *</Label>
              <Textarea
                id="edit-note"
                value={editNote}
                onChange={e => setEditNote(e.target.value)}
                placeholder="Es. Correzione importo come da accordo del 11/05"
                data-testid="edit-note-input"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Annulla</Button>
            <Button onClick={handleEditSave} data-testid="confirm-edit-btn">Salva Modifica</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog - shows modification history */}
      <Dialog open={showHistoryDialog} onOpenChange={(v) => { setShowHistoryDialog(v); if (!v) setHistoryEntry(null); }}>
        <DialogContent data-testid="history-dialog">
          <DialogHeader>
            <DialogTitle>Storico Modifiche Compenso</DialogTitle>
            <DialogDescription>Lista delle modifiche di importo apportate dall'admin.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {(historyEntry?.modification_history || []).length === 0 && (
              <p className="text-sm text-gray-500">Nessuna modifica registrata.</p>
            )}
            {(historyEntry?.modification_history || []).slice().reverse().map((h, idx) => (
              <div key={idx} className="border rounded-lg p-3 bg-gray-50 text-xs space-y-1">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-800">{h.user_name || 'Admin'}</span>
                  <span className="text-gray-500">{h.date ? format(new Date(h.date), 'dd/MM/yyyy HH:mm') : ''}</span>
                </div>
                {Math.abs((h.new_amount ?? 0) - (h.old_amount ?? 0)) > 0.001 && (
                  <div className="text-gray-700">
                    Importo: <span className="font-semibold text-red-600 line-through">{fmtCurrency(h.old_amount)}</span> → <span className="font-semibold text-emerald-600">{fmtCurrency(h.new_amount)}</span>
                  </div>
                )}
                {(h.changed_fields || []).map((cf, i) => (
                  <div key={i} className="text-gray-700">{cf}</div>
                ))}
                {h.note && <div className="text-gray-600 italic">"{h.note}"</div>}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistoryDialog(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={v => { setShowCreateDialog(v); if (!v) setProjectSearch(''); }}>
        <DialogContent className="max-w-lg" data-testid="create-compensation-dialog">
          <DialogHeader>
            <DialogTitle>Nuovo Compenso{!isAdmin && ' (Manuale)'}</DialogTitle>
            <DialogDescription>
              {isAdmin ? 'Crea un compenso per qualsiasi utente' : 'Inserisci un compenso manuale'}
            </DialogDescription>
          </DialogHeader>

          {isAdmin ? (
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Utente</Label>
                <Select value={newEntryAdmin.user_id} onValueChange={v => setNewEntryAdmin(p => ({ ...p, user_id: v }))}>
                  <SelectTrigger data-testid="admin-create-user"><SelectValue placeholder="Seleziona utente" /></SelectTrigger>
                  <SelectContent>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Progetto</Label>
                <Select value={newEntryAdmin.project_id} onValueChange={v => { setNewEntryAdmin(p => ({ ...p, project_id: v })); setProjectSearch(''); }}>
                  <SelectTrigger data-testid="admin-create-project"><SelectValue placeholder="Cerca progetto..." /></SelectTrigger>
                  <SelectContent>
                    <div className="px-2 pb-2">
                      <Input
                        placeholder="Cerca progetto..."
                        value={projectSearch}
                        onChange={e => setProjectSearch(e.target.value)}
                        className="h-7 text-xs"
                        data-testid="project-search-input"
                        onKeyDown={e => e.stopPropagation()}
                      />
                    </div>
                    {projects.filter(p => !projectSearch || p.name.toLowerCase().includes(projectSearch.toLowerCase())).map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Tipologia</Label>
                  <Select value={newEntryAdmin.compensation_type_id} onValueChange={v => setNewEntryAdmin(p => ({ ...p, compensation_type_id: v }))}>
                    <SelectTrigger data-testid="admin-create-type"><SelectValue placeholder="Tipologia" /></SelectTrigger>
                    <SelectContent>
                      {compTypes.filter(t => t.is_active !== false).map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.name} ({t.bucket})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Mese Riferimento</Label>
                  <Input type="month" value={newEntryAdmin.reference_month} onChange={e => setNewEntryAdmin(p => ({ ...p, reference_month: e.target.value }))} data-testid="admin-create-month" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Importo</Label>
                <Input type="number" step="0.01" value={newEntryAdmin.amount} onWheel={e => e.target.blur()} onChange={e => setNewEntryAdmin(p => ({ ...p, amount: e.target.value }))} data-testid="admin-create-amount" placeholder="0.00" />
              </div>
              <div>
                <Label className="text-xs">Riferimento / Descrizione</Label>
                <Input value={newEntryAdmin.reference_info} onChange={e => setNewEntryAdmin(p => ({ ...p, reference_info: e.target.value }))} placeholder="Es. Rettifica compenso..." data-testid="admin-create-ref" />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Progetto</Label>
                <Select value={newEntry.project_id} onValueChange={v => { setNewEntry(p => ({ ...p, project_id: v })); setProjectSearch(''); }}>
                  <SelectTrigger data-testid="user-create-project"><SelectValue placeholder="Cerca progetto..." /></SelectTrigger>
                  <SelectContent>
                    <div className="px-2 pb-2">
                      <Input
                        placeholder="Cerca progetto..."
                        value={projectSearch}
                        onChange={e => setProjectSearch(e.target.value)}
                        className="h-7 text-xs"
                        onKeyDown={e => e.stopPropagation()}
                      />
                    </div>
                    {projects.filter(p => !projectSearch || p.name.toLowerCase().includes(projectSearch.toLowerCase())).map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Tipologia Compenso</Label>
                <Select value={newEntry.compensation_type_id} onValueChange={v => setNewEntry(p => ({ ...p, compensation_type_id: v }))}>
                  <SelectTrigger data-testid="user-create-type"><SelectValue placeholder="Seleziona tipologia" /></SelectTrigger>
                  <SelectContent>
                    {compTypes.filter(t => t.is_active !== false).map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name} ({t.bucket})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Importo</Label>
                  <Input type="number" step="0.01" value={newEntry.amount} onWheel={e => e.target.blur()} onChange={e => setNewEntry(p => ({ ...p, amount: e.target.value }))} data-testid="user-create-amount" placeholder="0.00" />
                </div>
                <div>
                  <Label className="text-xs">Mese</Label>
                  <Input type="month" value={newEntry.reference_month} onChange={e => setNewEntry(p => ({ ...p, reference_month: e.target.value }))} data-testid="user-create-month" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Descrizione (opzionale)</Label>
                <Textarea value={newEntry.description} onChange={e => setNewEntry(p => ({ ...p, description: e.target.value }))} placeholder="Descrizione del compenso..." data-testid="user-create-description" />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Annulla</Button>
            <Button onClick={handleCreate} data-testid="submit-create-btn">Crea Compenso</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SearchableSelect({ value, onChange, options, placeholder, allLabel, testId, className }) {
  const [open, setOpen] = useState(false);
  const selected = options.find(o => o.value === value);
  const displayText = value === 'all' || !value ? (allLabel || 'Tutti') : (selected?.label || value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className={`h-9 text-sm justify-between font-normal px-3 ${className || ''}`} data-testid={testId}>
          <span className="truncate">{displayText}</span>
          <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="start">
        <Command>
          <CommandInput placeholder={`Cerca ${(placeholder || '').toLowerCase()}...`} className="h-9 text-sm" data-testid={`${testId}-search`} />
          <CommandList>
            <CommandEmpty>Nessun risultato</CommandEmpty>
            <CommandGroup>
              <CommandItem value="__all__" onSelect={() => { onChange('all'); setOpen(false); }}>
                <Check className={`mr-2 h-3.5 w-3.5 ${value === 'all' || !value ? 'opacity-100' : 'opacity-0'}`} />
                {allLabel || 'Tutti'}
              </CommandItem>
              {options.map(o => (
                <CommandItem key={o.value} value={o.label} onSelect={() => { onChange(o.value); setOpen(false); }}>
                  <Check className={`mr-2 h-3.5 w-3.5 ${value === o.value ? 'opacity-100' : 'opacity-0'}`} />
                  {o.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}